import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useMemo } from "react";
import { ApiError } from "../models/ApiResponse";
import { FortnoxIntegrationStatus } from "../models/FortnoxIntegrationStatus";
import { MailTemplate } from "../models/MailTemplate";
import { NotificationTemplate, OrgNotificationSettings } from "../models/OrgNotificationSetting";
import Organisation, {
	CompetenceResponse,
	CustomFieldDefinition,
	CustomFieldEntity,
	DocumentInfo,
	DocumentResponse,
} from "../models/Organisation";
import { OrganisationCompetenceUpdate } from "../models/OrganisationCompetenceUpdate";
import { QueryOpts } from "./api-hooks";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";
import { useAuthStore } from "../stores/auth.store";
import { sortCustomFieldDefinitions } from "../components/Shared/CustomFields/customFields.utils";

export function useOrganisation(query: { [key: string]: string } = {}) {
	const authStore = useAuthStore();

	return useQuery(
		["organisation", query],
		() =>
			apiStore
				.get<Organisation>({
					path: "/organisation/" + authStore.orgId,
					isAuthRequired: true,
					query: query as any,
				})
				.then((d) => d.data),
		{
			// The organisation rarely changes mid-session: serve the cached copy for
			// 30 min before considering it stale, and keep it in cache (and persisted)
			// for a day so it survives reloads. Mutations that change the org
			// invalidate ["organisation"], forcing an immediate refetch.
			staleTime: 30 * 60 * 1000,
			cacheTime: 24 * 60 * 60 * 1000,
			// Invalidation only reaches the tab the mutation ran in — an org edit
			// made in another tab (or by a colleague) would otherwise sit stale
			// for up to 30 min. "always" bypasses staleTime on tab focus, so
			// returning to a tab always revalidates the org entity.
			refetchOnWindowFocus: "always",
			// No org id means logged out (ContactContextMenu keeps this hook
			// mounted outside the auth gate) — don't fetch with a dead token.
			enabled: !!authStore.orgId,
		}
	);
}

export function useOrganisationNotificationSettings() {
	const authStore = useAuthStore();

	return useQuery(
		["organisation", "notification-settings"],
		() =>
			apiStore
				.get<{ templates: NotificationTemplate[]; settings: OrgNotificationSettings }>({
					path: "/organisation/" + authStore.orgId + "/notification-settings",
					isAuthRequired: true,
					query: {},
				})
				.then((d) => d.data),
		{
			staleTime: 60 * 1000,
		}
	);
}

export function useUpdateOrganisationCompetences() {
	const authStore = useAuthStore();

	const queryClient = useQueryClient();

	return useMutation<any, ApiError, OrganisationCompetenceUpdate>(
		(update) =>
			apiStore
				.put({
					path: `/organisation/${authStore.orgId}/competence`,
					isAuthRequired: true,
					body: update,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				// Competence names/archived state are resolved client-side from
				// cached trees, so invalidate every query carrying one — not just
				// the organisation: the job details/create pages read the tree via
				// useCompetencesByCustomer and the consultant filters/recruitment/
				// document pages via useCompetence (keyed "organization").
				queryClient.invalidateQueries(["organisation"]);
				queryClient.invalidateQueries(["organization", "competence"]);
				queryClient.invalidateQueries(["customer", "competences"]);
			},
			onError: () => {
				toast.errorGeneric();
			},
		}
	);
}

export function useMailTemplate(id: string) {
	return useQuery(
		["organisation", "mail-templates", id],
		() =>
			apiStore
				.get<MailTemplate>({
					path: `/mail/template/${id}`,
					isAuthRequired: true,
					query: {},
				})
				.then((d) => d.data),

		{
			enabled: !!id,
			// staleTime: 60 * 1000,
		}
	);
}

export function useDocuments(
	enabled: boolean = true,
	query: {
		page: number;
		pageSize: number;
		sort?: "metadata.created";
		order: number;
		keyword: string;
	},
	opts: QueryOpts = {}
) {
	return useQuery(
		["organization", "documents"],
		() =>
			apiStore
				.get<DocumentResponse>({
					path: `/document`,
					isAuthRequired: true,
					query: query,
				})
				.then((d) => {
					return d.data;
				}),

		{
			keepPreviousData: true,
			...opts,
			// staleTime: 60 * 1000,
		}
	);
}

export function useCompetence(enabled: boolean = true) {
	const authStore = useAuthStore();

	return useQuery(
		["organization", "competence", authStore.orgId],
		() =>
			apiStore
				.get<CompetenceResponse>({
					path: `/organisation/${authStore.orgId}/competences`,
					isAuthRequired: true,
					query: {},
				})
				.then((d) => d.data),

		{
			enabled: enabled,
		}
	);
}

export function useDocumentInfo(enabled: boolean = true, documentId?: string) {
	const authStore = useAuthStore();

	return useQuery(
		["organization", "documentInfo", authStore.orgId, documentId],
		() =>
			apiStore
				.get<DocumentInfo>({
					path: `/document/${documentId}`,
					isAuthRequired: true,
					query: {},
				})
				.then((d) => d.data),

		{
			enabled: enabled,
		}
	);
}

export function useMailTemplates(ids: string[]) {
	const { data, ...rest } = useQuery(
		["organisation", "mail-templates"],
		() =>
			Promise.all(
				ids.map((id) =>
					apiStore
						.get<MailTemplate>({
							path: `/mail/template/${id}`,
							isAuthRequired: true,
							query: {},
						})
						.then((d) => d.data)
				)
			),
		{
			staleTime: 60 * 1000,
			initialData: [],
			enabled: ids.length > 0,
		}
	);

	return {
		templates: data,
		...rest,
	};
}

export function useUpdateMailTemplate() {
	const queryClient = useQueryClient();

	return useMutation<any, ApiError, Partial<MailTemplate> & { id: string }>(
		({ id, ...params }) =>
			apiStore
				.put({
					path: `/mail/template/${id}`,
					isAuthRequired: true,
					body: { html: params.html, subject: params.subject },
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries(["organisation", "mail-templates"]);
			},
			onError: () => {
				toast.errorGeneric();
			},
		}
	);
}

/** Which org tag taxonomy a tag-type CRUD call targets. Maps 1:1 to the gateway path segment. */
export type TagTaxonomy = "consultant" | "customer" | "job";

/**
 * Create a tag type in one of the org's tag taxonomies
 * (`consultantTagTypes` / `customerTagTypes` / `jobTagTypes`).
 * The server slugifies `key` from `name.sv` when omitted.
 */
export function useCreateTagType() {
	const authStore = useAuthStore();
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiError,
		{
			taxonomy: TagTaxonomy;
			name: { sv: string };
			key?: string;
			validityDuration?: number;
			color?: string;
			showInListViews?: boolean;
		}
	>(
		({ taxonomy, ...body }) =>
			apiStore
				.post({
					path: `/organisation/${authStore.orgId}/tag-type/${taxonomy}`,
					isAuthRequired: true,
					body,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries(["organisation"]);
			},
			onError: () => {
				toast.errorGeneric();
			},
		}
	);
}

/** Rename / re-configure a tag type. Renames propagate automatically (entities store only the key). */
export function useUpdateTagType() {
	const authStore = useAuthStore();
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiError,
		{
			taxonomy: TagTaxonomy;
			key: string;
			name?: { sv: string };
			validityDuration?: number;
			color?: string;
			showInListViews?: boolean;
		}
	>(
		({ taxonomy, key, ...body }) =>
			apiStore
				.put({
					path: `/organisation/${authStore.orgId}/tag-type/${taxonomy}/${key}`,
					isAuthRequired: true,
					body,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries(["organisation"]);
			},
			onError: () => {
				toast.errorGeneric();
			},
		}
	);
}

/**
 * Permanently delete an archived competence. Fails with error code
 * `COMPETENCE_IN_USE` while any consultant holds it or any job references
 * it — no generic error toast
 * here so the caller can surface that case itself.
 */
export function useDeleteOrganisationCompetence() {
	const authStore = useAuthStore();
	const queryClient = useQueryClient();

	return useMutation<any, ApiError, OrganisationCompetenceUpdate["key"]>(
		(key) =>
			apiStore
				.put({
					path: `/organisation/${authStore.orgId}/competence`,
					isAuthRequired: true,
					body: { key, update: { delete: true } },
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["organisation"]);
				queryClient.invalidateQueries(["organization", "competence"]);
				queryClient.invalidateQueries(["customer", "competences"]);
			},
		}
	);
}

/**
 * Delete a tag type — CASCADE deletes the tag from all entities carrying it.
 * Deleting a consultant tag still used in an active job's requirements fails
 * with error code `TAG_TYPE_IN_USE` — no generic error toast here so callers
 * can surface that case themselves.
 */
export function useDeleteTagType() {
	const authStore = useAuthStore();
	const queryClient = useQueryClient();

	return useMutation<any, ApiError, { taxonomy: TagTaxonomy; key: string }>(
		({ taxonomy, key }) =>
			apiStore
				.delete({
					path: `/organisation/${authStore.orgId}/tag-type/${taxonomy}/${key}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["organisation"]);
			},
		}
	);
}

/**
 * The org's non-archived custom field definitions for one entity, sorted by
 * `order` (absent sorts last, then insertion order). Empty until the
 * organisation has loaded — callers hide the section entirely when this is
 * empty.
 */
export function useCustomFieldDefinitions(entity: CustomFieldEntity): CustomFieldDefinition[] {
	const { data: organisation } = useOrganisation();

	return useMemo(
		() =>
			sortCustomFieldDefinitions(
				(organisation?.customFieldDefinitions ?? []).filter(
					(definition) => definition.entity === entity && !definition.archived
				)
			),
		[organisation?.customFieldDefinitions, entity]
	);
}

export function useUpdateOrganisation() {
	const authStore = useAuthStore();
	const queryClient = useQueryClient();

	return useMutation<any, ApiError, Partial<Organisation>>(
		({ id, ...changes }) =>
			apiStore
				.put({
					path: `/organisation/${authStore.orgId}`,
					isAuthRequired: true,
					body: changes,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries(["organisation"]);
				queryClient.refetchQueries({
					queryKey: ["organisation"],
					predicate: (query) => query.queryKey[0] === "organisation",
				});
			},
			onError: () => {
				toast.errorGeneric();
			},
		}
	);
}

export function useUpdateOrganisationNotificationTemplate() {
	const authStore = useAuthStore();
	const queryClient = useQueryClient();

	return useMutation<any, ApiError, Partial<NotificationTemplate> & { id: string }>(
		(template) => {
			// Build body object with only provided fields to avoid overwriting with empty strings
			const body: any = {};
			if (template.body !== undefined) body.body = template.body;
			if (template.emailSubject !== undefined) body.emailSubject = template.emailSubject;
			if (template.pushTitle !== undefined) body.pushTitle = template.pushTitle;
			if (template.description !== undefined) body.description = template.description;
			if (template.humanReadableName !== undefined)
				body.humanReadableName = template.humanReadableName;
			if (template.tags !== undefined) body.tags = template.tags;

			return apiStore
				.put({
					path: `/organisation/${authStore.orgId}/template/${template.id}`,
					isAuthRequired: true,
					body,
				})
				.then((d) => d.data);
		},

		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries(["organisation"]);
			},
			onError: () => {
				toast.errorGeneric();
			},
		}
	);
}

export function useFortnoxIntegrationStatus() {
	return useQuery(["organisation", "fortnox"], () =>
		apiStore
			.get<FortnoxIntegrationStatus>({
				path: "/fortnox/status",
				isAuthRequired: true,
				query: {},
			})
			.then((d) => d.data)
	);
}

export function useFortnoxTextIntegration() {
	return useMutation<{ success: boolean }, ApiError, { type: "invoice" | "salary" }>((body) =>
		apiStore
			.post({
				path: `/fortnox/test`,
				isAuthRequired: true,
				body,
			})
			.then((d) => d.data)
	);
}

export function useUploadDocuments() {
	const queryClient = useQueryClient();

	return useMutation(
		(params: {
			branches?: Array<string>;
			mimeType: string;
			title: string;
			type: string;
			url: string;
		}) => {
			if (params.branches?.length === 0) {
				delete params.branches;
			}

			return apiStore
				.post({
					path: `/document`,
					isAuthRequired: true,
					body: params,
				})
				.then((d) => d.data);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["organization", "documents"]);
			},
		}
	);
}

export function useUpdateDocuments() {
	const queryClient = useQueryClient();

	return useMutation(
		(params: {
			data: {
				branches: Array<string>;
				mimeType: string;
				title: string;
				url: string;
			};
			documentId: string;
		}) =>
			apiStore
				.put({
					path: `/document/${params.documentId}`,
					isAuthRequired: true,
					body: params.data,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["organization", "documents"]);
				queryClient.invalidateQueries(["organization", "documentInfo"]);
			},
		}
	);
}

export function useDeleteDocument() {
	const queryClient = useQueryClient();

	return useMutation(
		(documentId: string) =>
			apiStore
				.delete({
					path: `/document/${documentId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["organization", "documents"]);
			},
		}
	);
}

export function useOrganisationNotificationTemplates(params: { tag?: string } = {}) {
	const authStore = useAuthStore();

	return useQuery(
		["organisation", "notification-templates", params],
		() =>
			apiStore
				.get<{ templates: NotificationTemplate[] }>({
					path: "/organisation/" + authStore.orgId + "/notification-templates",
					isAuthRequired: true,
					query: {
						tag: params.tag,
					},
				})
				.then((d) => d.data),
		{
			// staleTime: 60 * 1000,
			initialData: { templates: [] },
		}
	);
}

export function useSendTestNotification() {
	const authStore = useAuthStore();

	return useMutation<any, ApiError, { templateId: string; userEmail?: string }>(
		({ templateId, userEmail }) =>
			apiStore
				.post({
					path: `/organisation/${authStore.orgId}/template/${templateId}/test`,
					isAuthRequired: true,
					body: {
						userEmail,
					},
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				toast.success(t("organisation.notifications.testSent"));
			},
			onError: (error) => {
				if (error.detail?.includes("User not found")) {
					toast.error(t("organisation.notifications.userNotFound"));
				} else {
					toast.errorGeneric();
				}
			},
		}
	);
}

export function useCreateNotificationTemplate() {
	const authStore = useAuthStore();
	const queryClient = useQueryClient();

	return useMutation<
		NotificationTemplate,
		ApiError,
		{ name: string; humanReadableName?: string; description?: string }
	>(
		(template) =>
			apiStore
				.post({
					path: `/organisation/${authStore.orgId}/template`,
					isAuthRequired: true,
					body: {
						name: template.name,
						humanReadableName: template.humanReadableName,
						description: template.description,
						tags: ["recruitment"],
						channels: { mail: true },
					},
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries(["organisation", "notification-settings"]);
				// The recruitment messaging modals list templates via a separate
				// query — refresh it so a just-created template is selectable.
				queryClient.invalidateQueries(["organisation", "notification-templates"]);
			},
			onError: (error) => {
				if (error.detail?.includes("already exists")) {
					toast.error("En mall med detta namn finns redan");
				} else {
					toast.errorGeneric();
				}
			},
		}
	);
}

export interface ConsultantManager {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	active: boolean;
	roles: string[];
}

export function useConsultantManagers(keyword?: string, enabled: boolean = true) {
	const authStore = useAuthStore();

	const { data, ...rest } = useQuery(
		["organisation", "consultant-managers", authStore.orgId, keyword],
		() =>
			apiStore
				.get<{ users: ConsultantManager[] }>({
					path: `/organisation-account`,
					isAuthRequired: true,
					query: {
						view: "select_option",
						roles: "organisation_consultant_manager",
						organisationId: authStore.orgId,
						keyword: keyword || undefined,
					},
				})
				.then((d) => d.data),
		{
			enabled: enabled,
			staleTime: 60 * 1000,
		}
	);

	return {
		consultantManagers: data?.users || [],
		...rest,
	};
}

export function useConsultantManager(managerId?: string, enabled: boolean = true) {
	const { data, ...rest } = useQuery(
		["organisation", "consultant-manager", managerId],
		() =>
			apiStore
				.get<ConsultantManager>({
					path: `/organisation-account/${managerId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			enabled: !!managerId && enabled,
			staleTime: 60 * 1000,
		}
	);

	return {
		consultantManager: data,
		...rest,
	};
}
